import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import styled from "styled-components";
import { MdClose, MdRefresh, MdDelete } from "react-icons/md";

Modal.setAppElement("#root");

// Styled Components
const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 15px;
    border-bottom: 2px solid #fc7242;
    margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: #757575;
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    transition: color 0.2s;

    &:hover {
        color: #333;
    }
`;

const ModalBody = styled.div`
    max-height: 500px;
    overflow-y: auto;
`;

const InfoRow = styled.div`
    margin-bottom: 20px;
`;

const InfoLabel = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #757575;
    margin-bottom: 8px;
`;

const InfoValue = styled.div`
    font-size: 14px;
    color: #333;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 5px;
    border: 1px solid #ebebeb;
    word-break: break-word;
    white-space: pre-wrap;
`;

const EmailContentBox = styled.div`
    font-size: 14px;
    color: #333;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 5px;
    border: 1px solid #ebebeb;
    min-height: 150px;
    max-height: 300px;
    overflow-y: auto;
    word-break: break-word;
    white-space: pre-wrap;
`;

const EmailTypeBadge = styled.span`
    display: inline-block;
    padding: 6px 14px;
    border-radius: 14px;
    font-size: 13px;
    font-weight: 500;
    background-color: ${props => props.type === 'ORDER' ? '#e3f2fd' : '#f3e5f5'};
    color: ${props => props.type === 'ORDER' ? '#1976d2' : '#7b1fa2'};
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 25px;
    padding-top: 20px;
    border-top: 1px solid #ebebeb;
`;

const ActionButton = styled.button`
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
    background-color: ${props => {
        if (props.variant === 'retry') return '#28a745';
        if (props.variant === 'delete') return '#dc3545';
        return '#6c757d';
    }};
    color: white;

    &:hover {
        background-color: ${props => {
            if (props.variant === 'retry') return '#218838';
            if (props.variant === 'delete') return '#c82333';
            return '#5a6268';
        }};
    }
`;

const FailedEmailScheduleDetailModal = ({ isOpen, setIsOpen, emailData, onRetry, onDelete }) => {
    const { t } = useTranslation();

    if (!emailData) return null;

    const customStyles = {
        overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000
        },
        content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "700px",
            padding: "30px",
            borderRadius: "10px",
            border: "none",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => setIsOpen(false)}
            style={customStyles}
            contentLabel="Failed Email Schedule Detail"
        >
            <ModalHeader>
                <ModalTitle>{t("failedEmailSchedule.detailTitle")}</ModalTitle>
                <CloseButton onClick={() => setIsOpen(false)}>
                    <MdClose size={24} />
                </CloseButton>
            </ModalHeader>

            <ModalBody>
                {/* Email Type */}
                <InfoRow>
                    <InfoLabel>{t("failedEmailSchedule.emailType")}</InfoLabel>
                    <div>
                        <EmailTypeBadge type={emailData.emailType}>
                            {t(`failedEmailSchedule.emailTypes.${emailData.emailType}`)}
                        </EmailTypeBadge>
                    </div>
                </InfoRow>

                {/* Email Title */}
                <InfoRow>
                    <InfoLabel>{t("failedEmailSchedule.emailTitle")}</InfoLabel>
                    <InfoValue>{emailData.emailTitle}</InfoValue>
                </InfoRow>

                {/* Register Time */}
                <InfoRow>
                    <InfoLabel>{t("failedEmailSchedule.registerTime")}</InfoLabel>
                    <InfoValue>
                        {new Date(emailData.registerTime).toLocaleString()}
                    </InfoValue>
                </InfoRow>

                {/* Failure Reason */}
                <InfoRow>
                    <InfoLabel>{t("failedEmailSchedule.failureReason")}</InfoLabel>
                    <InfoValue>{emailData.failureReason}</InfoValue>
                </InfoRow>

                {/* Email Content */}
                <InfoRow>
                    <InfoLabel>{t("failedEmailSchedule.emailContent")}</InfoLabel>
                    <EmailContentBox>
                        {emailData.emailContent}
                    </EmailContentBox>
                </InfoRow>
            </ModalBody>

            <ModalFooter>
                <ActionButton
                    variant="retry"
                    onClick={() => onRetry(emailData)}
                >
                    <MdRefresh size={18} />
                    {t("failedEmailSchedule.retry")}
                </ActionButton>
                <ActionButton
                    variant="delete"
                    onClick={() => onDelete(emailData)}
                >
                    <MdDelete size={18} />
                    {t("failedEmailSchedule.delete")}
                </ActionButton>
                <ActionButton onClick={() => setIsOpen(false)}>
                    {t("button.close")}
                </ActionButton>
            </ModalFooter>
        </Modal>
    );
};

export default FailedEmailScheduleDetailModal;
